import { Label } from '@/components/ui/label';

/**
 * Pick several from a short list, as toggleable pills.
 *
 * The onboarding forms already had this markup inline for "species you train" — it is the
 * console's established way of asking for more than one answer, so the three fields the client
 * asked to make multi-select (cremation offerings, restaurant kinds, training modes) reuse it
 * rather than introducing checkboxes that would look like a different app.
 *
 * Pills rather than a multi-select dropdown on purpose: every one of these lists is under ten
 * items, and a dropdown hides what is currently chosen behind a click — which is the single
 * thing a vendor is checking when they come back to fix their registration.
 */
export function ChipMultiSelect<T extends string>({
  label,
  hint,
  options,
  selected,
  on_toggle,
  testid,
  disabled,
}: {
  label: string;
  /** Shown beside the label — use it for limits or for what an empty selection means. */
  hint?: string;
  options: readonly { value: T; label: string }[];
  selected: readonly T[];
  on_toggle: (value: T) => void;
  testid: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label>
        {label}
        {hint && <span className="ml-1.5 font-normal text-muted-foreground">{hint}</span>}
      </Label>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {options.map((o) => {
          const active = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              data-testid={`${testid}-${o.value}`}
              /* Read by the QA suites, and by anything asserting on what is selected —
                 the class list is styling and should not be load-bearing. */
              data-checked={active}
              // A pill is a real toggle, so it announces itself as one rather than as a
              // button whose state a screen reader has to infer from its colour.
              role="checkbox"
              aria-checked={active}
              disabled={disabled}
              onClick={() => on_toggle(o.value)}
              className={
                'rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-50 ' +
                (active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-input bg-card text-foreground hover:bg-muted')
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
